import CartContext from 'apps/Diner/contexts/Cart/CartContext'
import { useRouter } from 'next/router'
import { useEffect, useState, useMemo, useCallback } from 'react'
import {
  CreateCart,
  useCreateCartMutation,
  OrderType
} from '__generated__/types'
import { useUserSessionContext } from 'contexts/UserSession/UserSessionContext'
import { logger } from 'utils/logger'
import useOrderType from 'apps/Diner/hooks/useOrderType'
import useSnackbar from 'hooks/useSnackbar'
import { useTracking } from 'contexts/Tracking/TrackingContext'

interface Props {
  children: JSX.Element
}

const CARTS_STORAGE_KEY = 'uuids'

export const cartStorageKey = (
  merchantLocationId: string,
  orderType: OrderType,
  tableId?: string,
  venueId?: string | null | undefined
): string => {
  return [merchantLocationId, orderType, tableId, venueId || 'novenue'].join(
    '-'
  )
}

export const storeCartUuid = (
  merchantLocationId: string,
  orderType: OrderType,
  tableId: string,
  venueId: string | null | undefined,
  cartUuid: string
): void => {
  try {
    const uuids = JSON.parse(localStorage.getItem(CARTS_STORAGE_KEY) || '{}')
    // Retain the cart uuids created for other tables at this merchant
    // location, and add this one to the object. This way if the user
    // rescans a QR code they don't lose an in progress cart.
    const key = cartStorageKey(merchantLocationId, orderType, tableId, venueId)
    uuids[key] = cartUuid
    localStorage.setItem(CARTS_STORAGE_KEY, JSON.stringify(uuids))
  } catch (error) {}
}

export const getCartUuid = (
  merchantLocationId: string,
  orderType: OrderType,
  tableId: string,
  venueId: string | undefined
): string | undefined => {
  try {
    const uuids = JSON.parse(localStorage.getItem(CARTS_STORAGE_KEY) || '{}')
    const key = cartStorageKey(merchantLocationId, orderType, tableId, venueId)
    return uuids[key]
  } catch (error) {}
}

export const deleteCartUuid = (
  merchantLocationId: string,
  orderType: OrderType,
  tableId: string,
  venueId: string | undefined
): void => {
  try {
    const uuids = JSON.parse(localStorage.getItem(CARTS_STORAGE_KEY) || '{}')
    const key = cartStorageKey(merchantLocationId, orderType, tableId, venueId)
    delete uuids[key]
    localStorage.setItem(CARTS_STORAGE_KEY, JSON.stringify(uuids))
  } catch (error) {}
}

const CartProvider = ({ children }: Props): JSX.Element => {
  const [cartUuid, setCartUuid] = useState<string>()
  const router = useRouter()
  const merchantLocationId = router.query.merchantLocationId as string
  const { authenticated } = useUserSessionContext()
  const { orderType } = useOrderType()
  const tableId = (router.query.tableId as string) || orderType
  const venueId = (router.query.venueId as string) || undefined
  const { enqueueSnackbar } = useSnackbar()
  const { utmParams } = useTracking()

  const [createCart] = useCreateCartMutation({
    onCompleted: (data) => {
      const cart = data.createCart
      if (cart) {
        storeCartUuid(
          cart.merchantLocationId,
          cart.orderType,
          cart.tableId,
          cart.venueId,
          cart.uuid
        )
        setCartUuid(cart.uuid)
      }
    }
  })

  const recreateCart = useCallback(async () => {
    try {
      const variables: CreateCart.Variables = {
        merchantLocationId,
        tableId: tableId as string,
        receiptOrigin: window.location.origin,
        orderType: orderType as OrderType,
        ...utmParams
      }
      if (venueId) {
        variables.venueId = venueId
      }
      const response = await createCart({ variables })
      const newCart = response.data?.createCart
      return newCart
    } catch (error) {
      logger.error(error)
    }
  }, [createCart, merchantLocationId, tableId, venueId, orderType, utmParams])

  const removeCartUuid = useCallback(() => {
    if (orderType && tableId) {
      deleteCartUuid(merchantLocationId, orderType, tableId, venueId)
    }
    setCartUuid(undefined)
  }, [merchantLocationId, orderType, tableId, venueId])

  useEffect(() => {
    // On any page rendered underneath the CartProvider--MenuPage,
    // MenuItemPage, CheckoutPage, etc., if a merchantLocationId and tableId are
    // defined, get an existing cart for that table, or create a new one.
    if (merchantLocationId && tableId && orderType) {
      const uuid = getCartUuid(merchantLocationId, orderType, tableId, venueId)
      if (uuid) {
        // A cart was previously created for this merchant and table.
        // Setting the cart uuid here will trigger the GetCart query.
        setCartUuid(uuid)
      } else if (authenticated && !uuid) {
        // No cartUuid in localStorage, create a new cart
        const variables: CreateCart.Variables = {
          merchantLocationId,
          tableId,
          receiptOrigin: window.location.origin,
          orderType: orderType,
          ...utmParams
        }
        if (venueId) {
          variables.venueId = venueId
        }
        createCart({ variables }).catch(() => {
          // Timing issues in the API have caused the first request to
          // periodically fail, so retry once.
          setTimeout(() => {
            createCart({ variables }).catch((error) => {
              enqueueSnackbar(error, { variant: 'error' })
              logger.error(error)
            })
          }, 500)
        })
      }
    }
  }, [
    merchantLocationId,
    tableId,
    orderType,
    createCart,
    authenticated,
    venueId,
    recreateCart,
    enqueueSnackbar,
    utmParams
  ])

  const contextValue = useMemo(() => {
    return { cartUuid, recreateCart, removeCartUuid }
  }, [cartUuid, recreateCart, removeCartUuid])

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  )
}

export default CartProvider
